import { ACTIVITY_PAYLOAD_SCHEMAS } from "@/content/registry-core/activity-payload-schemas";
import type { LevelDefinition } from "@/domain";
import {
  HINT_STAGES,
  type ActiveHintStage
} from "@/game/feedback";
import type { SequenceHint } from "@/game/templates/sequence";

import {
  getPictureCharacterFeedbackCue,
  getSoundBubbleFeedbackCue,
  getStorySequenceFeedbackCue
} from "./chinese-feedback-cues";
import {
  defineCurriculumVoiceCue,
  type CurriculumVoiceCue
} from "./cue";
import {
  getEnglishChoiceFeedbackCue,
  getEnglishRhythmFeedbackCue
} from "./english-feedback-cues";
import {
  FOLLOW_DIRECTIONS_PROGRESS_CUE,
  FOLLOW_DIRECTIONS_REPLAYED_CUE,
  FOLLOW_DIRECTIONS_UNDONE_CUE,
  getFollowDirectionsFeedbackCue,
  getFriendshipHintCue,
  getTargetSearchFeedbackCue
} from "./focus-social-feedback-cues";
import {
  getDailyRoutineFeedbackCue,
  getSchoolbagFeedbackCue,
  SCHOOLBAG_PROGRESS_CUE
} from "./habits-feedback-cues";
import {
  getForestMarketHintCue,
  getForestMarketRetryCue,
  getNumberTrainHintCue,
  getNumberTrainRetryCue,
  getPatternHintCue,
  getPatternRetryCue
} from "./math-feedback-cues";
import type {
  CurriculumAudioLocale,
  CurriculumVoiceSynthesis
} from "./types";

export const LETTER_SOUND_PRONUNCIATIONS = {
  f: { exampleWord: "fish", ipa: "f" },
  l: { exampleWord: "leaf", ipa: "l" },
  m: { exampleWord: "moon", ipa: "m" },
  n: { exampleWord: "nest", ipa: "n" },
  r: { exampleWord: "rabbit", ipa: "ɹ" },
  s: { exampleWord: "sun", ipa: "s" }
} as const;

type LetterSoundToken = keyof typeof LETTER_SOUND_PRONUNCIATIONS;

const ACTIVE_HINT_STAGES = HINT_STAGES.filter(
  (stage): stage is ActiveHintStage => stage !== "none"
);

const FEEDBACK_KINDS = ["hint", "retry"] as const;

const SOUND_BUBBLE_SYNTHESIS_OVERRIDES: Readonly<Record<string, string>> = {
  "chinese-sound-bubbles-1": "妈",
  "chinese-sound-bubbles-2": "兔",
  "chinese-sound-bubbles-3": "马",
  "chinese-sound-bubbles-4": "宝",
  "chinese-sound-bubbles-5": "桥",
  "chinese-sound-bubbles-6": "学校"
};

export interface CurriculumVoiceRequirement {
  readonly path: string;
  readonly text: string;
  readonly locale: CurriculumAudioLocale;
  readonly synthesis: CurriculumVoiceSynthesis;
  readonly surfaces: readonly string[];
}

export interface ReviewedVoiceException {
  readonly id: string;
  readonly reason: "learning-target" | "non-text-visual";
  readonly description: string;
}

export interface LevelVoiceCoverage {
  readonly levelId: string;
  readonly requirements: readonly CurriculumVoiceRequirement[];
  readonly exceptions: readonly ReviewedVoiceException[];
}

interface MutableVoiceRequirement {
  readonly path: string;
  readonly text: string;
  readonly locale: CurriculumAudioLocale;
  readonly synthesis: CurriculumVoiceSynthesis;
  readonly surfaces: Set<string>;
}

interface CoverageBuilder {
  readonly level: LevelDefinition;
  readonly requirements: Map<string, MutableVoiceRequirement>;
  readonly exceptions: ReviewedVoiceException[];
}

function addRequirement(
  builder: CoverageBuilder,
  path: string,
  text: string,
  surface: string,
  locale: CurriculumAudioLocale = "zh-CN",
  synthesis: CurriculumVoiceSynthesis = { kind: "text", value: text }
): void {
  const cue = defineCurriculumVoiceCue(path, text);
  const existing = builder.requirements.get(cue.audioPath);

  if (existing !== undefined) {
    if (
      existing.text !== cue.text ||
      existing.locale !== locale ||
      existing.synthesis.kind !== synthesis.kind ||
      existing.synthesis.value !== synthesis.value
    ) {
      throw new Error(
        "Conflicting voice coverage for " + cue.audioPath +
          " in " + builder.level.id
      );
    }

    existing.surfaces.add(surface);
    return;
  }

  builder.requirements.set(cue.audioPath, {
    path: cue.audioPath,
    text: cue.text,
    locale,
    synthesis,
    surfaces: new Set([surface])
  });
}

function addCue(
  builder: CoverageBuilder,
  cue: CurriculumVoiceCue,
  surface: string,
  locale: CurriculumAudioLocale = "zh-CN"
): void {
  addRequirement(
    builder,
    cue.audioPath,
    cue.text,
    surface,
    locale
  );
}

function addException(
  builder: CoverageBuilder,
  exception: ReviewedVoiceException
): void {
  if (builder.exceptions.some(({ id }) => id === exception.id)) {
    throw new Error(
      "Duplicate voice exception " + exception.id +
        " for " + builder.level.id
    );
  }

  builder.exceptions.push(exception);
}

function requireAudioPath(
  path: string | undefined,
  surface: string,
  levelId: string
): string {
  if (path === undefined) {
    throw new Error("Missing " + surface + " audio for " + levelId);
  }

  return path;
}

function isLetterSoundToken(value: string): value is LetterSoundToken {
  return Object.hasOwn(LETTER_SOUND_PRONUNCIATIONS, value);
}

function createPhonemeSynthesis(
  token: LetterSoundToken
): CurriculumVoiceSynthesis {
  const pronunciation = LETTER_SOUND_PRONUNCIATIONS[token];
  const ssml =
    '<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" ' +
    'xml:lang="en-US"><prosody rate="-15%"><phoneme alphabet="ipa" ph="' +
    pronunciation.ipa +
    '">' +
    token +
    "</phoneme></prosody></speak>";

  return { kind: "ssml", value: ssml };
}

function getDailyRoutineSequenceHints(
  correctOrder: readonly string[]
): SequenceHint[] {
  return [
    { stage: "gentle-hint", targetIndex: 0 },
    {
      stage: "reduced-choices",
      targetIndex: 0,
      candidateItemIds: correctOrder.slice(0, 2)
    },
    { stage: "replay-demonstration", order: [...correctOrder] }
  ];
}

function classifyChineseCoverage(builder: CoverageBuilder): void {
  const { activityType, id, payload: unknownPayload } = builder.level;

  switch (activityType) {
    case "sound-bubbles": {
      const payload =
        ACTIVITY_PAYLOAD_SCHEMAS.chinese["sound-bubbles"].parse(
          unknownPayload
        );
      const answerToken =
        SOUND_BUBBLE_SYNTHESIS_OVERRIDES[id] ?? payload.spokenText;

      addRequirement(
        builder,
        payload.soundAudio,
        payload.spokenText,
        "stimulus",
        "zh-CN",
        { kind: "text", value: answerToken }
      );
      for (const stage of ACTIVE_HINT_STAGES) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getSoundBubbleFeedbackCue(payload, stage, kind),
            kind + ":" + stage
          );
        }
      }
      addException(builder, {
        id: "candidate-pronunciations",
        reason: "learning-target",
        description: "候选字音本身是听辨目标，朗读会泄露答案。"
      });
      return;
    }
    case "picture-character-match": {
      const payload =
        ACTIVITY_PAYLOAD_SCHEMAS.chinese[
          "picture-character-match"
        ].parse(unknownPayload);

      for (const stage of ACTIVE_HINT_STAGES) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getPictureCharacterFeedbackCue(id, payload, stage, kind),
            kind + ":" + stage
          );
        }
      }
      addException(builder, {
        id: "character-candidates",
        reason: "learning-target",
        description: "候选汉字是图文匹配目标，不增加泄题式朗读。"
      });
      return;
    }
    case "story-sequence": {
      const payload =
        ACTIVITY_PAYLOAD_SCHEMAS.chinese["story-sequence"].parse(
          unknownPayload
        );
      addRequirement(
        builder,
        payload.narrationAudio,
        payload.narration,
        "story-narration"
      );
      const hints: readonly SequenceHint[] = [
        { stage: "gentle-hint", targetIndex: 0 },
        {
          stage: "reduced-choices",
          targetIndex: 0,
          candidateItemIds: payload.items.slice(0, 2).map(({ id }) => id)
        },
        { stage: "replay-demonstration", order: payload.correctOrder }
      ];

      for (const hint of hints) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getStorySequenceFeedbackCue(payload, kind, hint),
            kind + ":" + hint.stage
          );
        }
      }
      addException(builder, {
        id: "story-card-reading",
        reason: "learning-target",
        description: "故事排序卡的阅读内容属于语文学习目标。"
      });
      return;
    }
    default:
      throw new Error("Unclassified Chinese activity " + activityType);
  }
}

function classifyMathCoverage(builder: CoverageBuilder): void {
  const { activityType, payload: unknownPayload } = builder.level;

  switch (activityType) {
    case "number-train": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.math["number-train"].parse(
        unknownPayload
      );
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getNumberTrainHintCue(payload, stage),
          "hint:" + stage
        );
      }
      addCue(builder, getNumberTrainRetryCue(payload), "retry");
      break;
    }
    case "forest-market": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.math["forest-market"].parse(
        unknownPayload
      );
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getForestMarketHintCue(payload, stage),
          "hint:" + stage
        );
      }
      addCue(builder, getForestMarketRetryCue(payload), "retry");
      break;
    }
    case "pattern-bridge": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.math["pattern-bridge"].parse(
        unknownPayload
      );
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getPatternHintCue(payload, stage),
          "hint:" + stage
        );
      }
      addCue(builder, getPatternRetryCue(payload), "retry");
      break;
    }
    default:
      throw new Error("Unclassified math activity " + activityType);
  }

  addException(builder, {
    id: "visual-answer-options",
    reason: "non-text-visual",
    description: "数量、图案或空间位置由可区分视觉表达，无文字判断依赖。"
  });
}

function classifyEnglishCoverage(builder: CoverageBuilder): void {
  const { activityType, id, payload: unknownPayload } = builder.level;

  switch (activityType) {
    case "letter-sound": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.english["letter-sound"].parse(
        unknownPayload
      );
      const token = payload.answerTokens[0];
      if (!isLetterSoundToken(token)) {
        throw new Error("Missing letter-sound pronunciation for " + id);
      }
      if (LETTER_SOUND_PRONUNCIATIONS[token].exampleWord !== payload.exampleWord) {
        throw new Error("Letter-sound example drift for " + id);
      }
      addRequirement(
        builder,
        payload.soundAudio,
        token,
        "letter-sound-model",
        "en-US",
        createPhonemeSynthesis(token)
      );
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getEnglishChoiceFeedbackCue(stage),
          "feedback:" + stage
        );
      }
      addException(builder, {
        id: "candidate-letter-sounds",
        reason: "learning-target",
        description: "候选字母音是评估目标，朗读会直接泄露答案。"
      });
      return;
    }
    case "picture-word-match": {
      const payload =
        ACTIVITY_PAYLOAD_SCHEMAS.english["picture-word-match"].parse(
          unknownPayload
        );
      addRequirement(
        builder,
        payload.wordAudio,
        payload.answerTokens[0],
        "answer-model",
        "en-US"
      );
      for (const [token, audioPath] of Object.entries(payload.tokenAudio)) {
        addRequirement(
          builder,
          audioPath,
          token,
          "choice:" + token,
          "en-US"
        );
      }
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getEnglishChoiceFeedbackCue(stage),
          "feedback:" + stage
        );
      }
      return;
    }
    case "rhythm-phrases": {
      const payload =
        ACTIVITY_PAYLOAD_SCHEMAS.english["rhythm-phrases"].parse(
          unknownPayload
        );
      addRequirement(
        builder,
        payload.modelAudio,
        payload.caption,
        "phrase-model",
        "en-US"
      );
      for (const [token, audioPath] of Object.entries(payload.tokenAudio)) {
        addRequirement(
          builder,
          audioPath,
          token,
          "token:" + token,
          "en-US"
        );
      }
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getEnglishRhythmFeedbackCue(stage),
          "feedback:" + stage
        );
      }
      return;
    }
    default:
      throw new Error("Unclassified English activity " + activityType);
  }
}

function classifyHabitsCoverage(builder: CoverageBuilder): void {
  const { activityType, id, payload: unknownPayload } = builder.level;

  switch (activityType) {
    case "daily-routine": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.habits["daily-routine"].parse(
        unknownPayload
      );
      for (const item of payload.items) {
        addRequirement(
          builder,
          requireAudioPath(item.audio, "routine item", id),
          item.label,
          "item:" + item.id
        );
      }
      const hints = getDailyRoutineSequenceHints(payload.correctOrder);
      for (const hint of hints) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getDailyRoutineFeedbackCue(id, payload, kind, hint),
            kind + ":" + hint.stage
          );
        }
      }
      return;
    }
    case "pack-schoolbag": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.habits["pack-schoolbag"].parse(
        unknownPayload
      );
      for (const item of payload.items) {
        addRequirement(
          builder,
          requireAudioPath(item.audio, "schoolbag item", id),
          item.label,
          "item:" + item.id
        );
      }
      for (const dropZone of payload.dropZones) {
        addRequirement(
          builder,
          requireAudioPath(dropZone.audio, "schoolbag drop zone", id),
          dropZone.label,
          "drop-zone:" + dropZone.id
        );
      }
      for (const stage of ACTIVE_HINT_STAGES) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getSchoolbagFeedbackCue(kind, stage),
            kind + ":" + stage
          );
        }
      }
      addCue(builder, SCHOOLBAG_PROGRESS_CUE, "progress");
      return;
    }
    case "school-scenarios": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS.habits[
        "school-scenarios"
      ].parse(unknownPayload);
      addRequirement(
        builder,
        requireAudioPath(payload.narration.audio, "scenario narration", id),
        payload.narration.text,
        "scene-narration"
      );
      for (const choice of payload.choices) {
        addRequirement(
          builder,
          requireAudioPath(choice.audio, "scenario choice", id),
          choice.text,
          "choice:" + choice.id
        );
        addRequirement(
          builder,
          requireAudioPath(
            choice.supportiveFeedback.audio,
            "scenario choice feedback",
            id
          ),
          choice.supportiveFeedback.text,
          "choice-feedback:" + choice.id
        );
      }
      if (payload.followUp !== undefined) {
        addRequirement(
          builder,
          requireAudioPath(payload.followUp.audio, "scenario follow-up", id),
          payload.followUp.text,
          "follow-up"
        );
      }
      return;
    }
    default:
      throw new Error("Unclassified habits activity " + activityType);
  }
}

function classifyFocusSocialCoverage(builder: CoverageBuilder): void {
  const { activityType, id, payload: unknownPayload } = builder.level;

  switch (activityType) {
    case "target-search": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS["focus-social"][
        "target-search"
      ].parse(unknownPayload);
      for (const round of payload.rounds) {
        addRequirement(
          builder,
          requireAudioPath(round.promptAudio, "search round prompt", id),
          round.prompt,
          "round:" + round.id
        );
      }
      for (const stage of ACTIVE_HINT_STAGES) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getTargetSearchFeedbackCue(stage, kind),
            kind + ":" + stage
          );
        }
      }
      addException(builder, {
        id: "search-targets",
        reason: "non-text-visual",
        description: "搜索目标由颜色、形状和图案等可区分视觉线索表达。"
      });
      return;
    }
    case "follow-directions": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS["focus-social"][
        "follow-directions"
      ].parse(unknownPayload);
      for (const step of payload.steps) {
        addRequirement(
          builder,
          requireAudioPath(step.audio, "direction step", id),
          step.caption,
          "step:" + step.id
        );
      }
      for (const stage of ACTIVE_HINT_STAGES) {
        for (const kind of FEEDBACK_KINDS) {
          addCue(
            builder,
            getFollowDirectionsFeedbackCue(stage, kind),
            kind + ":" + stage
          );
        }
      }
      addCue(builder, FOLLOW_DIRECTIONS_PROGRESS_CUE, "progress");
      addCue(builder, FOLLOW_DIRECTIONS_UNDONE_CUE, "undo");
      addCue(builder, FOLLOW_DIRECTIONS_REPLAYED_CUE, "replay");
      addException(builder, {
        id: "direction-targets",
        reason: "non-text-visual",
        description: "指令目标由当前步骤语音和可区分视觉共同表达。"
      });
      return;
    }
    case "friendship-stories": {
      const payload = ACTIVITY_PAYLOAD_SCHEMAS["focus-social"][
        "friendship-stories"
      ].parse(unknownPayload);
      addRequirement(
        builder,
        requireAudioPath(payload.narration.audio, "friendship narration", id),
        payload.narration.text,
        "story-narration"
      );
      for (const choice of payload.choices) {
        const choiceAudio = requireAudioPath(
          choice.audio,
          "friendship choice",
          id
        );
        const feedbackAudio = requireAudioPath(
          choice.supportiveFeedback.audio,
          "friendship choice feedback",
          id
        );
        addRequirement(
          builder,
          choiceAudio,
          choice.text,
          "choice:" + choice.id
        );
        addRequirement(
          builder,
          feedbackAudio,
          choice.supportiveFeedback.text,
          "choice-feedback:" + choice.id
        );

      }
      if (payload.followUp !== undefined) {
        addRequirement(
          builder,
          requireAudioPath(payload.followUp.audio, "friendship follow-up", id),
          payload.followUp.text,
          "follow-up"
        );
      }
      for (const stage of ACTIVE_HINT_STAGES) {
        addCue(
          builder,
          getFriendshipHintCue(stage),
          "hint:" + stage
        );
      }
      return;
    }
    default:
      throw new Error("Unclassified focus-social activity " + activityType);
  }
}

export function classifyCurriculumVoiceCoverage(
  level: LevelDefinition
): LevelVoiceCoverage {
  const builder: CoverageBuilder = {
    level,
    requirements: new Map(),
    exceptions: []
  };
  addRequirement(
    builder,
    requireAudioPath(level.promptAudio, "level prompt", level.id),
    level.instruction,
    "level-prompt"
  );

  switch (level.domain) {
    case "chinese":
      classifyChineseCoverage(builder);
      break;
    case "math":
      classifyMathCoverage(builder);
      break;
    case "english":
      classifyEnglishCoverage(builder);
      break;
    case "habits":
      classifyHabitsCoverage(builder);
      break;
    case "focus-social":
      classifyFocusSocialCoverage(builder);
      break;
  }

  return {
    levelId: level.id,
    requirements: [...builder.requirements.values()]
      .sort((left, right) => left.path.localeCompare(right.path))
      .map((requirement) => ({
        path: requirement.path,
        text: requirement.text,
        locale: requirement.locale,
        synthesis: requirement.synthesis,
        surfaces: [...requirement.surfaces].sort()
      })),
    exceptions: [...builder.exceptions].sort((left, right) =>
      left.id.localeCompare(right.id)
    )
  };
}
